<template>
  <transition name="fade">
    <div
      v-if="visible"
      class="modal-overlay"
      @click.self="handleClose"
    >
      <div class="modal-card">
        <div class="modal-header">
          <div>
            <p class="modal-title">
              {{ isEditMode ? 'Editar usuario' : 'Agregar usuario' }}
            </p>
            <p class="modal-subtitle">
              {{ isEditMode ? 'Actualiza la información del usuario.' : 'Completa los datos para crear un nuevo usuario.' }}
            </p>
          </div>
        </div>

        <form @submit.prevent="handleSubmit" class="modal-form">
          <!-- Nombre -->
          <div class="modal-field">
            <label class="modal-label">Nombre</label>
            <input
              v-model.trim="nombre"
              type="text"
              autocomplete="name"
              placeholder="Nombre y apellido"
              class="ios-input"
            />
          </div>

          <!-- Email -->
          <div class="modal-field">
            <label class="modal-label">Correo electrónico</label>
            <input
              v-model.trim="email"
              type="email"
              autocomplete="email"
              placeholder="ejemplo@mail.com"
              class="ios-input"
            />
          </div>

          <!-- Rol (solo para superadmin / admin) -->
          <div
            v-if="canManageRoles"
            class="modal-field"
          >
            <label class="modal-label">Rol</label>
            <select
              v-model="role"
              class="ios-input modal-select"
            >
              <option value="">-- Selecciona un rol --</option>
              <option
                v-for="option in availableRoles"
                :key="option.value"
                :value="option.value"
              >
                {{ option.text }}
              </option>
            </select>
          </div>

          <!-- Contraseña -->
          <div class="modal-field">
            <label class="modal-label">
              Contraseña
              <span class="modal-label-hint">
                {{
                  isEditMode
                    ? '(dejar vacío para mantener la actual)'
                    : '(requerida para crear el usuario)'
                }}
              </span>
            </label>
            <input
              v-model="password"
              type="password"
              autocomplete="new-password"
              placeholder="Mínimo 10 caracteres"
              class="ios-input"
            />
            <p class="modal-label-hint">
              Debe incluir al menos una mayúscula, una minúscula y un número.
            </p>
          </div>

          <!-- Confirmar contraseña -->
          <div class="modal-field">
            <label class="modal-label">Confirmar contraseña</label>
            <input
              v-model="confirmPassword"
              type="password"
              autocomplete="new-password"
              placeholder="Repite tu contraseña"
              class="ios-input"
            />
          </div>

          <!-- Acciones -->
          <div class="modal-actions">
            <button
              type="button"
              class="ios-secondary-button modal-btn-cancel"
              @click="handleClose"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="ios-primary-button modal-btn-submit"
              :disabled="saving"
            >
              <span v-if="!saving">
                {{ isEditMode ? 'Guardar cambios' : 'Crear usuario' }}
              </span>
              <span v-else class="loader-dot"></span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script>
import { useToast } from 'vue-toastification';
import { createUser, updateUser } from './usuarios.service';

export default {
  name: 'UserFormModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create', // 'create' | 'edit'
    },
    user: {
      type: Object,
      default: null,
    },
    currentUserRole: {
      type: String,
      default: null,
    },
    availableRoles: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'saved'],
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      nombre: '',
      email: '',
      role: '',
      password: '',
      confirmPassword: '',
      saving: false,
    };
  },
  computed: {
    isEditMode() {
      return this.mode === 'edit';
    },
    canManageRoles() {
      return (
        this.currentUserRole === 'superadministrador' ||
        this.currentUserRole === 'administrador'
      );
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initForm();
      }
    },
    user: {
      handler() {
        if (this.visible) {
          this.initForm();
        }
      },
      deep: true,
    },
  },
  methods: {
    initForm() {
      if (this.isEditMode && this.user) {
        this.nombre = this.user.nombre || '';
        this.email = this.user.email || '';
        this.role = this.user.role || '';
        this.password = '';
        this.confirmPassword = '';
      } else {
        this.nombre = '';
        this.email = '';
        this.role = '';
        this.password = '';
        this.confirmPassword = '';
      }
    },
    handleClose() {
    this.$emit('close');
    },
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    validatePassword(password) {
      if (!password || password.length < 10) return false;
      if (!/[A-Z]/.test(password)) return false;
      if (!/[a-z]/.test(password)) return false;
      if (!/[0-9]/.test(password)) return false;
      return true;
    },
    async handleSubmit() {
      const errores = [];

      if (!this.nombre.trim()) {
        errores.push('• Ingresa el nombre.');
      }

      if (!this.validateEmail(this.email)) {
        errores.push('• Ingresa un correo electrónico válido.');
      }

      if (this.canManageRoles && !this.role) {
        errores.push('• Selecciona un rol para el usuario.');
      }

      if (this.isEditMode) {
        if (this.password || this.confirmPassword) {
          if (this.password !== this.confirmPassword) {
            errores.push('• Las contraseñas no coinciden.');
          }
          if (!this.validatePassword(this.password)) {
            errores.push(
              '• La contraseña debe tener al menos 10 caracteres e incluir una mayúscula, una minúscula y un número.'
            );
          }
        }
      } else {
        // Crear usuario: contraseña obligatoria
        if (!this.password || !this.confirmPassword) {
          errores.push('• Ingresa y confirma la contraseña.');
        } else {
          if (this.password !== this.confirmPassword) {
            errores.push('• Las contraseñas no coinciden.');
          }
          if (!this.validatePassword(this.password)) {
            errores.push(
              '• La contraseña debe tener al menos 10 caracteres e incluir una mayúscula, una minúscula y un número.'
            );
          }
        }
      }

      if (errores.length) {
        this.toast?.error(errores.join('\n'));
        return;
      }

      this.saving = true;

      try {
        if (this.isEditMode && this.user) {
          const payload = {
            nombre: this.nombre,
            email: this.email,
          };
          if (this.canManageRoles) {
            payload.role = this.role;
          }
          if (this.password) {
            payload.password = this.password;
          }

          await updateUser(this.user.id_token, payload);
          this.toast?.success('Usuario actualizado correctamente.');
        } else {
          const payload = {
            nombre: this.nombre,
            email: this.email,
            password: this.password,
            role: this.canManageRoles ? this.role : undefined,
          };

          await createUser(payload);
          this.toast?.success('Usuario creado correctamente.');
        }

        this.$emit('saved');
        this.handleClose();
      } catch (error) {
        console.error('Error en guardar usuario:', error);
        this.toast?.error('Hubo un problema al guardar el usuario.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: radial-gradient(
    circle at 0 0,
    rgba(148, 163, 184, 0.25),
    rgba(15, 23, 42, 0.95)
  );
  backdrop-filter: blur(18px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
}

.modal-card {
  width: 100%;
  max-width: 520px;
  border-radius: 1.6rem;
  padding: 1.6rem 1.5rem 1.4rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.4),
      rgba(15, 23, 42, 0.97)
    );
  border: 1px solid rgba(148, 163, 184, 0.7);
  box-shadow:
    0 22px 55px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(15, 23, 42, 0.85);
}

.modal-header {
  margin-bottom: 1.1rem;
}

.modal-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #e5e7eb;
}

.modal-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.modal-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.modal-label {
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #cbd5f5;
}

.modal-label-hint {
  font-weight: 400;
  font-size: 0.7rem;
  letter-spacing: normal;
  text-transform: none;
  color: #9ca3af;
  margin-left: 0.25rem;
}

.ios-input {
  width: 100%;
  border-radius: 1rem;
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 0.7rem 0.95rem;
  font-size: 0.86rem;
  color: #e5e7eb;
  outline: none;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease,
    transform 0.12s ease;
  appearance: none;
}

.ios-input::placeholder {
  color: rgba(148, 163, 184, 0.9);
}

.ios-input:focus {
  border-color: rgba(52, 211, 153, 0.95);
  box-shadow:
    0 0 0 1px rgba(52, 211, 153, 0.7),
    0 18px 45px rgba(15, 23, 42, 0.85);
  background: rgba(15, 23, 42, 0.98);
  transform: translateY(-1px);
}

.modal-select {
  padding-right: 2.2rem;
  background-image: linear-gradient(
      45deg,
      transparent 50%,
      rgba(148, 163, 184, 0.9) 50%
    ),
    linear-gradient(
      135deg,
      rgba(148, 163, 184, 0.9) 50%,
      transparent 50%
    );
  background-position:
    calc(100% - 14px) 50%,
    calc(100% - 9px) 50%;
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 0.4rem;
}

/* Botones */
.ios-primary-button {
  border-radius: 999px;
  padding: 0.7rem 1rem;
  font-size: 0.86rem;
  font-weight: 600;
  background: linear-gradient(
    135deg,
    #f9fafb,
    #e5e7eb
  );
  color: #020617;
  border: none;
  box-shadow:
    0 12px 30px rgba(15, 23, 42, 0.7),
    0 0 0 1px rgba(148, 163, 184, 0.3);
  cursor: pointer;
  transition:
    transform 0.12s ease,
    box-shadow 0.18s ease,
    background 0.18s ease,
    opacity 0.18s ease;
}

.ios-primary-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow:
    0 16px 36px rgba(15, 23, 42, 0.85),
    0 0 0 1px rgba(148, 163, 184, 0.45);
}

.ios-primary-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow:
    0 10px 25px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(148, 163, 184, 0.35);
}

.ios-primary-button:disabled {
  opacity: 0.7;
  cursor: default;
}

.ios-secondary-button {
  border-radius: 999px;
  padding: 0.7rem 1rem;
  font-size: 0.86rem;
  font-weight: 500;
  background: rgba(15, 23, 42, 0.8);
  color: #e5e7eb;
  border: 1px solid rgba(148, 163, 184, 0.6);
  cursor: pointer;
  transition:
    transform 0.12s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease,
    border-color 0.18s ease;
}

.ios-secondary-button:hover {
  background: rgba(15, 23, 42, 0.95);
  border-color: rgba(148, 163, 184, 0.9);
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.85);
}

/* Loader */
.loader-dot {
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 999px;
  border: 2px solid rgba(15, 23, 42, 0.85);
  border-top-color: rgba(15, 23, 42, 0.1);
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
